import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTripDocument } from "../../../../context/TripDocumentContext";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import { SendTripDocumentRequest, TripPODResponse } from "../../../../types/api";
import { PODPayloadBufferIds } from "../../docPayloads";
import { useTripContext } from "../../../../context/tripsContext";
import { useAuth } from "../../../../utils/hooks/useAuth";
import { toast } from "react-toastify";
import { docSide } from "../../style";
import { useLocation, useParams } from "react-router-dom";
import { appInsights, trackFullPayload } from "../../../../utils/appInsights";
import { useUserFlowTracking } from "../../../../utils/hooks/useUserFlowTracking";
import { useDocumentNavigationData } from "../../../../utils/hooks/useDocumentNavigationData";
import { useNavigationContext } from "../../../../context/navigationContext";
import { RelatedLoad } from "../../../../types/api";

interface ISidebarProps {
  isOpen: boolean;
  callbackCloseSidebar: (shouldFetchDocuments: boolean) => void;
  callbackUpdateDocuments?: (documents: any) => void;
  index: number;
  maxIndex?: number;
  action: boolean;
  loadingTmsLocation?: string; // nuova prop
  onDocumentUploaded?: () => void; // nuova prop per il fetch dei documenti
  filteredDocuments?: TripPODResponse[]; // aggiunta prop per i documenti filtrati
}

const SidebarAddDocument: React.FC<ISidebarProps> = ({
  isOpen = false,
  callbackCloseSidebar,
  callbackUpdateDocuments,
  index,
  action,
  loadingTmsLocation = "",
  onDocumentUploaded,
  filteredDocuments = [] // default a array vuoto
}) => {
  const location = useLocation(); // Ottieni l'oggetto location
  const currentPath = location.pathname; // La path corrente
  const params = useParams(); // Ottieni i parametri dell'URL
  
  // Estrai il tripId dal pathname
  const tripIdFromUrl = currentPath.match(/\/tripdetails\/(\d+)/)?.[1];

  const [docName, setDocName] = useState<string>("");
  const [timestamp, setTimestamp] = useState<string>("");

  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { uploadPOD, isDocumentLoading, sendTripDocument, errorMessage } =
    useTripDocument();

  const { tripDetails, stops } = useTripContext();
  const { user } = useAuth();
  const { trackDocumentUploadFlow, trackInteraction } = useUserFlowTracking();
  const { getNavigationDataAsString } = useDocumentNavigationData();
  const { navigationSteps, sessionId } = useNavigationContext();

  // Trova i soli delivery stops e il delivery corrente
  const deliveryStops = stops?.filter((stop) => stop.relatedActivities?.some((activity: any) => activity.activityType === "DELIVERY")) || [];
  const currentDeliveryStop = deliveryStops[index];
  
  // Filtra i loading options per escludere quelli che hanno già documenti validi (tranne rejected)
  const getAvailableLoadingOptions = () => {
    if (!currentDeliveryStop || !filteredDocuments) return [];
    
    const debugInfo = {
      currentDeliveryStop: currentDeliveryStop?.tmsLocation,
      filteredDocumentsCount: filteredDocuments.length,
      filteredDocuments: filteredDocuments,
      originalLoads: currentDeliveryStop.relatedLoads?.map(load => ({
        tmsLocation: load.tmsLocation,
        involvedPartyName: load.involvedPartyName
      })) || [],
      analysis: [] as any[]
    };
    
    const availableOptions = currentDeliveryStop.relatedLoads?.filter((load) => {
      // Cerca documenti esistenti per questo punto di carico
      const existingDocs = filteredDocuments.filter((doc: TripPODResponse) => {
        const docLocation = doc.TESTO246_136;
        const expectedLocation = load.tmsLocation + "_" + currentDeliveryStop.tmsLocation;
        return docLocation && docLocation === expectedLocation;
      });
      
      // Controlla se ci sono documenti validi (DOCNUMBER > 0)
      const validDocs = existingDocs.filter((doc: TripPODResponse) => doc.DOCNUMBER && doc.DOCNUMBER > 0);
      const documentStatus = validDocs[0]?.STATO;
      
      // Considera i seguenti stati come "documento già presente e valido"
      const validDocumentStatuses = [
        "Profilo Parziale",
        "In Verifica", 
        "In Gestione",
        "Gestito",
        "Verificata",
        "Approvato"
      ];
      
      const isAvailable = existingDocs.length === 0 || 
                         validDocs.length === 0 || 
                         (documentStatus && (documentStatus === "Annullato" || documentStatus === "Errore"));
      
      // Aggiungi info per il debug
      debugInfo.analysis.push({
        loadTmsLocation: load.tmsLocation,
        loadInvolvedPartyName: load.involvedPartyName,
        existingDocsCount: existingDocs.length,
        validDocsCount: validDocs.length,
        documentStatus: documentStatus,
        isAvailable: isAvailable,
        existingDocs: existingDocs.map(doc => ({
          DOCNUMBER: doc.DOCNUMBER,
          STATO: doc.STATO,
          TESTO246_136: doc.TESTO246_136
        }))
      });
      
      return isAvailable;
    }) || [];
    
    // Log unico con tutte le informazioni
    console.log('[DEBUG] Loading Options Filter Analysis:', debugInfo);
    
    return availableOptions;
  };
  
  const availableLoadingOptions = getAvailableLoadingOptions();
  const loadingOptions = availableLoadingOptions;

  // selectedOption ora si inizializza con loadingTmsLocation se presente
  const [selectedOption, setSelectedOption] = useState<string>(loadingTmsLocation);
  const [hasManuallySelected, setHasManuallySelected] = useState<boolean>(false);

  // Aggiorna il valore selezionato quando cambia loadingTmsLocation o quando la sidebar si apre
  useEffect(() => {
    // Solo se la sidebar si apre e non è stata fatta una selezione manuale
    if (isOpen && !hasManuallySelected) {
      // Se c'è solo un loading option disponibile, selezionalo automaticamente
      if (loadingOptions.length === 1) {
        const autoSelectedOption = loadingOptions[0].tmsLocation;
        setSelectedOption(autoSelectedOption);
      } else if (loadingOptions.length === 0) {
        // Se non ci sono loading options, svuota la selezione
        setSelectedOption("");
      } else {
        setSelectedOption(loadingTmsLocation || "");
      }
    }
    
    // Log unico con tutte le informazioni del useEffect
    console.log('[DEBUG] SidebarAddDocument useEffect:', {
      isOpen,
      loadingTmsLocation,
      index,
      loadingOptionsCount: loadingOptions.length,
      availableLoadingOptionsCount: availableLoadingOptions.length,
      hasManuallySelected,
      selectedOption,
      autoSelected: loadingOptions.length === 1 ? loadingOptions[0]?.tmsLocation : null
    });
  }, [isOpen, loadingTmsLocation, index, loadingOptions, hasManuallySelected]);

  // Reset del flag quando la sidebar si chiude
  useEffect(() => {
    if (!isOpen) {
      setHasManuallySelected(false);
    }
  }, [isOpen]);

  // Funzioni helper per formattare i dati
  const formatDuration = (milliseconds: number): string => {
    if (milliseconds < 1000) return `${milliseconds}ms`;
    if (milliseconds < 60000) return `${Math.round(milliseconds / 1000)}s`;
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.round((milliseconds % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getPageName = (path: string): string => {
    const pathMap: { [key: string]: string } = {
      '/dashboard': 'Dashboard',
      '/services': 'Servizi',
      '/documents': 'Documenti',
      '/contracts': 'Contratti',
      '/trips': 'Viaggi',
      '/fleet-and-route': 'Flotta e Percorsi',
      '/settings': 'Impostazioni',
      '/profile': 'Profilo',
      '/trip': 'Dettagli Viaggio',
      '/trip-documents': 'Documenti Viaggio',
      '/error-page': 'Pagina Errore'
    };
    
    return pathMap[path] || path;
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('it-IT', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedOption(value);
    setHasManuallySelected(true); // Marca che è stata fatta una selezione manuale
    trackInteraction('select', 'loading_point_dropdown', 'change', {
      selectedValue: value,
      action: action
    });
  };

  const handleDropdownClick = () => {
    // Log unico per il dropdown
    console.log('[DEBUG] Dropdown interaction:', {
      selectedOption,
      stopsCount: stops?.length || 0,
      action
    });
  };

  // Funzione per validare il file
  const validateFile = (file: File): { isValid: boolean; errorMessage?: string } => {
    // Validazione estensione file
    const allowedExtensions = ['jpeg', 'jpe', 'jpg', 'png', 'tiff', 'tif', 'pdf'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        errorMessage: t("errors.fileValidation.unsupportedFormat", { 
          formats: allowedExtensions.join(', ').toUpperCase() 
        }) as string
      };
    }

    // Validazione dimensione file (20MB = 20 * 1024 * 1024 bytes)
    const maxSizeInBytes = 20 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return {
        isValid: false,
        errorMessage: t("errors.fileValidation.fileTooLarge", { 
          maxSize: "20MB" 
        }) as string
      };
    }

    return { isValid: true };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Validazione: se non è PKL (action = false) e non è selezionato un punto di carico, impedisci la selezione
    if (!action && !selectedOption) {
      toast.error(t("screens.trips.timeline.selectLoadingPointFirst") || "Seleziona prima un punto di carico");
      return;
    }

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validazione del file
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.error(validation.errorMessage || t("errors.fileValidation.invalidFile"));
        // Reset del file input
        if (e.target) {
          e.target.value = '';
        }
        return;
      }

      const currentTimestamp =
        new Date().toISOString().replace("T", " ").split(".")[0] + ".000";
      
      setSelectedFile(file);
      setDocName(file.name);
      setTimestamp(currentTimestamp);

      // Traccia l'inizio del flusso di upload del documento
      trackDocumentUploadFlow.startDocumentUpload(file.name, file.size, file.type, {
        action: action,
        hasStops: !!stops && stops.length > 0,
        selectedOption: selectedOption,
        tripId: tripDetails?.tmsObjectNumber,
        userEmail: user?.email
      });

      // Traccia l'interazione con il file input
      trackInteraction('file_input', 'document_upload', 'file_selected', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
    }
  };

  const handleDeleteFile = () => {
    if (selectedFile) {
      // Traccia l'interazione di eliminazione file
      trackInteraction('button', 'delete_file', 'click', {
        fileName: selectedFile.name
      });
    }
    setSelectedFile(null);
  };

  const extractTransportOrderKey = (transportOrderTMSKey: string): string => {
    if (!transportOrderTMSKey || transportOrderTMSKey === 'N/A' || transportOrderTMSKey === 'undefined') {
      return 'N/A';
    }
    const parts = transportOrderTMSKey.split("_");
    return parts[parts.length - 1];
  };

  const generateStrings = (stops: any[]) => {
    const results: { concatenated: string; orderKey: string }[] = [];
    
    if (!stops || stops.length === 0) {
      return results;
    }
    
    // Separate loading and delivery stops
    const loadingStops = stops?.filter((stop) => stop.relatedActivities?.some((activity: any) => activity.activityType === "LOAD")) || [];
    const deliveryStops = stops?.filter((stop) => stop.relatedActivities?.some((activity: any) => activity.activityType === "DELIVERY")) || [];
    
    // Get the specific delivery stop based on index
    const specificDeliveryStop = deliveryStops[index];
    
    if (!specificDeliveryStop) {
      return results;
    }
    
    // Get the loading points that are related to this specific delivery stop
    const relatedLoadingPoints = specificDeliveryStop.relatedLoads || [];
    
    if (relatedLoadingPoints.length === 0) {
      return results;
    }
    
    // Create a map to track unique loading points and their indices
    const uniqueLoadingPoints = new Map<string, { 
      involvedPartyName: string; 
      tmsLocation: string; 
      index: number;
      transportOrderTMSKey: string;
    }>();
    
    let globalIndex = 0;
    
    // Process only the loading points that are related to this specific delivery stop
    relatedLoadingPoints.forEach((relatedLoad: RelatedLoad) => {
      if (!uniqueLoadingPoints.has(relatedLoad.tmsLocation)) {
        uniqueLoadingPoints.set(relatedLoad.tmsLocation, {
          involvedPartyName: relatedLoad.involvedPartyName,
          tmsLocation: relatedLoad.tmsLocation,
          index: globalIndex,
          transportOrderTMSKey: (relatedLoad as any).transportOrderTMSKey || 'N/A'
        });
        globalIndex++;
      }
    });
    
    // Convert map to array and create results
    const uniqueLoadingPointsArray = Array.from(uniqueLoadingPoints.values());
    
    uniqueLoadingPointsArray.forEach((loadingPoint) => {
      const transportOrderKey = extractTransportOrderKey(
        loadingPoint.transportOrderTMSKey
      );
      
      // Create the combination for this specific delivery stop
      const concatenated = `${loadingPoint.tmsLocation}_${specificDeliveryStop.tmsLocation}`;
      
      results.push({
        concatenated: concatenated,
        orderKey: transportOrderKey,
      });
    });
    
    // Log unico per generateStrings
    console.log('[DEBUG] generateStrings result:', {
      index,
      stopsCount: stops?.length || 0,
      loadingStopsCount: loadingStops.length,
      deliveryStopsCount: deliveryStops.length,
      specificDeliveryStop: specificDeliveryStop?.tmsLocation,
      relatedLoadingPointsCount: relatedLoadingPoints.length,
      uniqueLoadingPointsCount: uniqueLoadingPointsArray.length,
      results
    });
    
    return results;
  };

  const [isDragging, setIsDragging] = useState(false);

  // Temporary debug useEffect
  useEffect(() => {
    // Debug delivery stops filtering
    const deliveryStops = stops?.filter((stop) => stop.relatedActivities?.some((activity: any) => activity.activityType === "DELIVERY")) || [];
    const loadingStops = stops?.filter((stop) => stop.relatedActivities?.some((activity: any) => activity.activityType === "LOAD")) || [];
    
    // Check if index is valid for delivery stops
    const isValidIndex = deliveryStops[index] !== undefined;
    
    // Single comprehensive debug log
    console.log('[DEBUG] SIDEBAR GRID ANALYSIS:', {
      // Basic props
      index,
      action,
      isOpen,
      
      // Stops analysis
      totalStops: stops?.length || 0,
      deliveryStopsCount: deliveryStops.length,
      loadingStopsCount: loadingStops.length,
      
      // Index validation
      isValidIndex,
      availableDeliveryIndices: deliveryStops.map((_, idx) => idx),
      
      // Current delivery stop (if valid)
      currentDeliveryStop: isValidIndex ? {
        tmsLocation: deliveryStops[index].tmsLocation,
        invPartyName: deliveryStops[index].invPartyName,
        relatedLoadsCount: deliveryStops[index].relatedLoads?.length || 0
      } : null,
      
      // All delivery stops for reference
      allDeliveryStops: deliveryStops.map((stop, idx) => ({
        index: idx,
        tmsLocation: stop.tmsLocation,
        invPartyName: stop.invPartyName,
        relatedLoadsCount: stop.relatedLoads?.length || 0
      })),
      
      // All loading stops for reference
      allLoadingStops: loadingStops.map((stop, idx) => ({
        index: idx,
        tmsLocation: stop.tmsLocation,
        invPartyName: stop.invPartyName
      }))
    });
  }, [stops, action, index, isOpen]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // Validazione: se non è PKL (action = false) e non è selezionato un punto di carico, impedisci il drop
    if (!action && !selectedOption) {
      toast.error(t("screens.trips.timeline.selectLoadingPointFirst") || "Seleziona prima un punto di carico");
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      // Validazione del file
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.error(validation.errorMessage || t("errors.fileValidation.invalidFile"));
        return;
      }

      const currentTimestamp =
        new Date().toISOString().replace("T", " ").split(".")[0] + ".000";
      
      setSelectedFile(file);
      setDocName(file.name);
      setTimestamp(currentTimestamp);

      // Traccia l'inizio del flusso di upload del documento via drag & drop
      trackDocumentUploadFlow.startDocumentUpload(file.name, file.size, file.type, {
        action: action,
        hasStops: !!stops && stops.length > 0,
        selectedOption: selectedOption,
        tripId: tripDetails?.tmsObjectNumber,
        userEmail: user?.email,
        uploadMethod: 'drag_drop'
      });

      // Traccia l'interazione con il drag & drop
      trackInteraction('drag_drop', 'document_upload', 'file_dropped', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
    }
  };



  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }
    if (!stops || stops.length === 0) {
      return;
    }
    
    // Traccia l'inizio dell'operazione di upload
    appInsights.trackEvent({
      name: 'SidebarAddDocument_Upload_Started',
      properties: {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type,
        action: action,
        hasStops: !!stops && stops.length > 0,
        selectedOption: selectedOption
      }
    });

    // Traccia il passaggio di processing del documento
    trackDocumentUploadFlow.trackDocumentProcessing('upload_processing', {
      fileName: selectedFile.name,
      action: action
    });
    
    try {
      const response = await uploadPOD(selectedFile);
      
      if (response && Array.isArray(response) && response.length > 0) {
        const tmsObjectNumber = (((currentDeliveryStop as any)?.tmsObjectNumber) || ((stops as any)?.[0]?.tmsObjectNumber) );
        const userMail = user ? user.email : "-";
        // Per PKL (action = true), non serve selezionare un punto di carico
        let concatenatedStrings;
        if (action) {
          concatenatedStrings = [{ concatenated: "PKL_GENERAL", orderKey: "PKL" }];
        } else if (selectedOption) {
          const selectedLoad = loadingOptions.find(l => l.tmsLocation === selectedOption);
          const transportOrderKey = selectedLoad ? extractTransportOrderKey((selectedLoad as any).transportOrderTMSKey || 'N/A') : 'N/A';
          concatenatedStrings = [{
            concatenated: `${selectedOption}_${currentDeliveryStop.tmsLocation}`,
            orderKey: transportOrderKey,
          }];
        } else {
          // Se non è selezionato nessun option, usa generateStrings per ottenere le opzioni disponibili
          concatenatedStrings = generateStrings(stops);
        }
        
        const selectedStop =
          concatenatedStrings[0]
            ? concatenatedStrings[0]
            : null;
        
        // Per PKL, non serve controllare selectedStop perché è sempre presente
        if (!action && !selectedStop) {
          return;
        }

        // Ottieni i dati di navigazione
        const navigationData = getNavigationDataAsString();
        
        const dynamicPayload: SendTripDocumentRequest = !action
        ? {
            ...PODPayloadBufferIds,
            document: {
              bufferIds: response,
            },
            fields: [
              ...PODPayloadBufferIds.fields.filter(
                (field) =>
                  ![
                    "TESTO227_131",
                    "TESTO246_136",
                    "DocName",
                    "Originale",
                    "TESTO265_131",
                    "DATA224_131",
                    "TESTO223_131",
                  ].includes(field.name)
              ),
              {
                value: tmsObjectNumber || 0,
                name: "TESTO227_131",
                externalId: "COD_VIAGGIO",
                className: "AdditionalFieldStringDTO",
              },
              {
                value:
                  concatenatedStrings[0]
                    .concatenated,
                name: "TESTO246_136",
                externalId: "MITTENTI_DESTINATARI",
                className: "AdditionalFieldStringDTO",
              },
              {
                value: docName,
                name: "DocName",
                className: "SubjectFieldDTO",
              },
              {
                value: selectedFile.name,
                name: "Originale",
                className: "OriginalFieldDTO",
              },
              {
                value: selectedStop?.orderKey || "N/A",
                name: "TESTO265_131",
                externalId: "COD_ORDINE",
                className: "AdditionalFieldStringDTO",
              },
              {
                value: timestamp,
                name: "DATA224_131",
                externalId: "DATAORAFOTO",
                className: "AdditionalFieldDateTimeDTO",
              },
              {
                value: userMail,
                name: "TESTO223_131",
                externalId: "ACCOUNT",
                className: "AdditionalFieldStringDTO",
              },
            ],
          }
        : {
          ...PODPayloadBufferIds,
          document: {
            bufferIds: response,
          },
          fields: [
            ...PODPayloadBufferIds.fields.filter(
              (field) =>
                ![
                  "TESTO227_131",
                  "TESTO246_136",
                  "DocName",
                  "Originale",
                  "TESTO265_131",
                  "DATA224_131",
                  "TESTO223_131",
                  "DocumentType",
                  "COMBO221_131",
                  "NUMERIC60_8",
                  "Stato",
                ].includes(field.name)
            ),
            {
              value: tmsObjectNumber || 0,
              name: "TESTO227_131",
              externalId: "COD_VIAGGIO",
              className: "AdditionalFieldStringDTO",
            },
            {
              value:
                concatenatedStrings[0]
                  .concatenated,
              name: "TESTO246_136",
              externalId: "MITTENTI_DESTINATARI",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: docName,
              name: "DocName",
              className: "SubjectFieldDTO",
            },
            {
              value: selectedFile.name,
              name: "Originale",
              className: "OriginalFieldDTO",
            },
            {
              value: selectedStop?.orderKey || "N/A",
              name: "TESTO265_131",
              externalId: "COD_ORDINE",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: timestamp,
              name: "DATA224_131",
              externalId: "DATAORAFOTO",
              className: "AdditionalFieldDateTimeDTO",
            },
            {
              value: userMail,
              name: "TESTO223_131",
              externalId: "ACCOUNT",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: 357,
              name: "DocumentType",
              className: "DocumentTypeFieldDTO",
            },
            {
              value: "PKL",
              name: "COMBO221_131",
              externalId: "TIPO_DOCUMENTO",
              className: "AdditionalFieldComboDTO",
            },
            {
              value: "Valid",
              name: "Stato",
              className: "StateFieldDTO",
            },
          ],
        };

        // Traccia il passaggio di invio del documento
        trackDocumentUploadFlow.trackDocumentProcessing('document_sending', {
          fileName: selectedFile.name,
          action: action,
          payloadSize: JSON.stringify(dynamicPayload).length
        });

        // Crea l'oggetto JSON con tutti i dati di navigazione visibili nel FAB
        const navigationInsightData = {
          sessione: sessionId,
          utente: userMail,
          passiTotali: navigationSteps.length,
          passi: navigationSteps.map((step: any, index: number) => ({
            numero: index + 1,
            nomePagina: getPageName(step.path),
            timestamp: formatTime(step.timestamp),
            path: step.path,
            parametri: step.searchParams || null,
            hash: step.hash || null,
            durataDalPassoPrecedente: step.duration ? formatDuration(step.duration) : null,
            tempoSullaPagina: step.timeOnPage && step.timeOnPage > 0 ? formatDuration(step.timeOnPage) : null,
            provenienza: step.referrer || null
          })),
          statistiche: {
            durataTotaleSessione: navigationSteps.length > 1 ? 
              formatDuration(navigationSteps[navigationSteps.length - 1].timestamp.getTime() - navigationSteps[0].timestamp.getTime()) : 
              '0s',
            pagineUniche: Array.from(new Set(navigationSteps.map((step: any) => step.path))).length
          }
        };

        // Insight per tracciare il payload completo della chiamata
        appInsights.trackEvent({
          name: 'SidebarAddDocument_SendTripDocument_Payload',
          properties: {
            fileName: selectedFile.name,
            action: action,
            tmsObjectNumber: tmsObjectNumber,
            userEmail: userMail,
            payload: JSON.stringify(dynamicPayload),
            timestamp: timestamp,
            docName: docName,
            concatenatedString: concatenatedStrings[0].concatenated,
            navigationInsightData: JSON.stringify(navigationInsightData, null, 2),
            tripDetailsResponse: tripDetails,
            stopsResponse: stops,
            tripId: tripIdFromUrl
          }
        });

        // Log unico per l'upload
        console.log('[DEBUG] Upload process:', {
          fileName: selectedFile.name,
          selectedOption,
          action,
          index,
          concatenatedStrings,
          selectedStop,
          tmsObjectNumber,
          userMail
        });
        
        try {
          const sendTripResponse = await sendTripDocument(dynamicPayload);
        } catch (sendError) {
          throw sendError;
        }

        // Chiama la callback per fare il fetch dei documenti aggiornati
        onDocumentUploaded?.();

        // Traccia il completamento del flusso di upload
        trackDocumentUploadFlow.completeDocumentUpload(undefined, undefined, {
          fileName: selectedFile.name,
          action: action,
          tripId: tmsObjectNumber,
          userEmail: userMail,
          success: true
        });

        toast.success("Documento inviato con successo");
      } else {
        // Traccia l'errore dell'upload
        appInsights.trackEvent({
          name: 'SidebarAddDocument_Upload_Error',
          properties: {
            fileName: selectedFile.name,
            errorType: 'InvalidResponse',
            response: response
          }
        });

        // Traccia il fallimento del flusso di upload
        trackDocumentUploadFlow.failDocumentUpload('InvalidResponse', {
          fileName: selectedFile.name,
          action: action,
          response: response
        });
        
        toast.error("Errore durante l'invio del documento");
      }
    } catch (error) {
      // Traccia l'errore generale
      appInsights.trackEvent({
        name: 'SidebarAddDocument_Upload_Exception',
        properties: {
          fileName: selectedFile.name,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          action: action
        }
      });

      // Traccia il fallimento del flusso di upload
      trackDocumentUploadFlow.failDocumentUpload(
        error instanceof Error ? error.message : 'Unknown error',
        {
          fileName: selectedFile.name,
          action: action
        }
      );
      
      toast.error("Errore durante il caricamento del file");
    } finally {
      callbackCloseSidebar(true);
      setSelectedFile(null);
    }
  };

  const onButtonClick = () => {
    // Validazione: se non è PKL (action = false) e non è selezionato un punto di carico, impedisci l'apertura del file input
    if (!action && !selectedOption) {
      toast.error(t("screens.trips.timeline.selectLoadingPointFirst") || "Seleziona prima un punto di carico");
      return;
    }

    inputRef.current?.click();
    
    // Traccia l'interazione con il pulsante di upload
    trackInteraction('button', 'upload_button', 'click', {
      action: action
    });
  };

  console.log('[DEBUG] SidebarAddDocument props:', { isOpen, loadingTmsLocation });
  return (
    <aside
      className={`flex flex-col transition z-[9999] fixed right-0 top-0 h-[100dvh] w-screen md:w-[520px] bg-white overflow-auto hide-scrollbar ${
        isOpen ? "translate-x-0 sidebar-shadow" : "translate-x-[100%]"
      }`}
    >
      {/* Header */}
      <div className="shrink-0 h-[88px] px-[30px] pt-[32px] pb-[24px] border-b flex justify-between items-center">
        <div className="text-lg font-bold">
          {t("common.sidebars.addContractDocument.heading")}
        </div>
        <div
          onClick={() => callbackCloseSidebar(false)}
          className="cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-8 h-8"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className="grow overflow-auto px-[30px] py-8">
        <input
          ref={inputRef}
          type="file"
          id="input-file-upload"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {!selectedFile && (
          <>
            <Box sx={docSide.box}>
              <div
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
             
                  width:'100%',
                }}
                className={`flex justify-center  ${
                  isDragging ? "border-dashed border-2 border-blue-500 rounded-lg m-1" : ""
                }`}
              >
   
    <button 
      onClick={onButtonClick} 
      className="upload-button" 
      style={{ 
      
        padding: "68px 12px",
        opacity: (!action && !selectedOption) ? 0.5 : 1,
        cursor: (!action && !selectedOption) ? "not-allowed" : "pointer"
      }}
      disabled={!action && !selectedOption}
    >
                  {t("common.sidebars.addContractDocument.dragNDropText")}
                </button>
              </div>
            </Box>
            {currentPath.includes("/tripdetails") &&
            !currentPath.includes("/documents") && !action ? (
              ""
            ) : !action ? (
              <FormControl fullWidth sx={{ marginTop: 2 }}>
                <Typography
                  id="select-loading-point-label"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "primary.main",
                    marginTop: "32px",
                    marginBottom: "4px",
                    textTransform: "uppercase",
                  }}
                >
                  {t("screens.trips.timeline.loadingPoint")}:
                </Typography>
                <select
                  id="loading-point-select"
                  value={selectedOption}
                  onChange={handleDropdownChange}
                  disabled={loadingOptions.length === 1}
                  style={{ 
                    padding: '8px', 
                    borderRadius: '4px', 
                    border: '1px solid #ccc',
                    backgroundColor: loadingOptions.length === 1 ? '#f5f5f5' : 'white',
                    color: loadingOptions.length === 1 ? '#666' : 'inherit'
                  }}
                >
                  <option value="">{t('screens.trips.timeline.selectLoadingPoint')}</option>
                  {loadingOptions.map((load) => (
                    <option key={load.tmsLocation} value={load.tmsLocation}>
                      {load.involvedPartyName}
                    </option>
                  ))}
                </select>

                {loadingOptions.length === 0 && (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "warning.main",
                      marginTop: "4px",
                      fontStyle: "italic"
                    }}
                  >
                    {t('screens.trips.timeline.noLoadingPoints') || "Nessun punto di carico disponibile"}
                  </Typography>
                )}
              </FormControl>
            ) : null}
          </>
        )}

        {selectedFile && (
          <Box sx={docSide.box1}>
            <Typography sx={docSide.title}>
              {t("screens.trips.tripDocuments.document")}:
            </Typography>
            <Box sx={docSide.box2}>
              <div className="mr-2">{selectedFile.name}</div>
              <div
                onClick={handleDeleteFile}
                className="cursor-pointer"
                title="Delete"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 7H5V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V7H6ZM10 19H8V10H10V19ZM16 19H14V10H16V19ZM16.618 4L15 2H9L7.382 4H3V6H21V4H16.618Z"
                    className="fill-red"
                  />
                </svg>
              </div>
            </Box>
            <Box sx={docSide.box3}>
              <Typography sx={docSide.description}>
                {t("screens.trips.tripDocuments.description")}
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
              />
            </Box>
          </Box>
        )}
      </div>
      <Box sx={docSide.box4}></Box>
      <div className="shrink-0 py-6 px-[30px] text-right border-t">
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={isDocumentLoading}
          sx={docSide.button}
        >
          {t("common.sidebars.saveButtonText")}
        </Button>
      </div>
    </aside>
  );
};

export default SidebarAddDocument;
